// contentSearch — the pure projection behind the library's CONTENT matches
// (library C3): hits from the server's full-text search (GET /v1/search),
// shown BELOW the client-side title matches, each with its snippet.
//
// The server matches ONE phrase, so the library searches each free word on
// its own and joins the answers here: a resource is a content match when
// EVERY word hits it somewhere (its title, an author, or its text) and it is
// not already a title match. "Fotosyntes kursplan" finds "Kursplan i biologi"
// whose text says "fotosyntes".
//
// The library module owns the read (`ContentSearch`, a `Remote` keyed by the
// words it answers); this module projects it + the query's current words +
// the visible pool into what the surface renders. A hit becomes a row only
// when its resource is in the pool (so the tabs, filters and the surface's
// visible kinds all apply). Pure, no I/O.

import { Remote } from "@bilda/utils";
import type { ExternalError } from "../../app/externalError.js";
import type { ContentHit, ContentSearchResult } from "../../app/ports.js";
import { textMatches } from "../SearchPage/searchRows.js";
import type { SearchResult, TextSegment } from "./searchModel.js";

// The server refuses shorter queries; they are never sent.
const CONTENT_QUERY_MIN = 2;

// What the server is asked for one word or "phrase" (null: too short).
export const contentQueryOf = (word: string): string | null => {
    const trimmed = word.replace(/^"|"$/gu, "").trim();
    return trimmed.length >= CONTENT_QUERY_MIN ? trimmed : null;
};

// The words worth a content search, in query order.
export const askedWords = (words: readonly string[]): readonly string[] =>
    words.filter((word) => contentQueryOf(word) !== null);

export const sameWords = (
    a: readonly string[],
    b: readonly string[],
): boolean => a.length === b.length && a.every((w, i) => w === b[i]);

// One word's answer.
export type WordHits = {
    readonly word: string;
    readonly result: ContentSearchResult;
};

// The content read for a list of words ([] = none asked).
export type ContentSearch = {
    readonly words: readonly string[];
    readonly hits: Remote.Remote<readonly WordHits[], ExternalError>;
};

type ContentRow = {
    readonly result: SearchResult;
    readonly hit: ContentHit;
    readonly snippetSegments: readonly TextSegment[];
};

// What the surface renders under the title matches.
export type ContentSection =
    | { readonly type: "hidden" }
    | { readonly type: "loading" }
    | { readonly type: "failed" }
    | { readonly type: "empty" }
    | {
          readonly type: "rows";
          readonly rows: readonly ContentRow[];
          readonly truncated: boolean;
      };

// before | MATCH | after, dropping empty parts.
export const snippetSegments = (
    snippet: ContentHit["snippet"],
): readonly TextSegment[] =>
    [
        { text: snippet.before, matched: false },
        { text: snippet.match, matched: true },
        { text: snippet.after, matched: false },
    ].filter((segment) => segment.text !== "");

// Does the (folded) word hit the row's title or one of its authors?
const fieldHit = (result: SearchResult, word: string): boolean =>
    textMatches(
        [word],
        [result.title, result.byline ?? "", ...(result.authors ?? [])],
    );

const joined = (
    answers: readonly WordHits[],
    pool: readonly SearchResult[],
    titleMatchIds: ReadonlySet<string>,
): ContentSection => {
    const byId = new Map(pool.map((r) => [r.id, r]));
    const ids = [
        ...new Set(
            answers.flatMap((a) => a.result.hits.map((h) => h.resourceId)),
        ),
    ];
    const rows = ids.flatMap((id): readonly ContentRow[] => {
        const result = byId.get(id);
        if (result === undefined || titleMatchIds.has(id)) return [];
        const hitFor = (a: WordHits): ContentHit | undefined =>
            a.result.hits.find((h) => h.resourceId === id);
        const holds = answers.every(
            (a) => hitFor(a) !== undefined || fieldHit(result, a.word),
        );
        const hit = answers.map(hitFor).find((h) => h !== undefined);
        return !holds || hit === undefined
            ? []
            : [{ result, hit, snippetSegments: snippetSegments(hit.snippet) }];
    });
    return rows.length === 0
        ? { type: "empty" }
        : {
              type: "rows",
              rows,
              truncated: answers.some((a) => a.result.truncated),
          };
};

export const contentSection = (
    content: ContentSearch,
    words: readonly string[],
    pool: readonly SearchResult[],
    titleMatchIds: ReadonlySet<string>,
): ContentSection => {
    const asked = askedWords(words);
    if (asked.length === 0) return { type: "hidden" };
    // The read for these words has not begun yet.
    if (!sameWords(content.words, asked)) return { type: "loading" };
    const rowsOf = (answers: readonly WordHits[]): ContentSection =>
        joined(answers, pool, titleMatchIds);
    return Remote.match<readonly WordHits[], ExternalError, ContentSection>({
        notAsked: () => ({ type: "loading" }),
        loading: () => ({ type: "loading" }),
        // The last answer stays while the next one is read.
        reloading: rowsOf,
        loaded: rowsOf,
        failed: () => ({ type: "failed" }),
    })(content.hits);
};

// The keyboard/selection order: title rows, then content rows.
export const navOrder = (
    titleResults: readonly SearchResult[],
    section: ContentSection,
): readonly SearchResult[] =>
    section.type === "rows"
        ? [...titleResults, ...section.rows.map((row) => row.result)]
        : titleResults;
